import type { ReactNode } from "react";
import { ChatRoomParticipantsBadges } from "@/features/chat/components/ChatRoomParticipantsBadges";
import { mapChatRoomsToListItems } from "@/features/chat/utils/chatRoomList";
import type { ChatRoomListItem } from "@/features/chat/components/ChatRoomListSection";
import type { ChatRoomResponse } from "@/lib/humhub-api/types";

type SearchRoomType = "user_dm" | "user_group";

type SearchResponse = {
  results?: ChatRoomResponse[];
};

type SearchState = {
  response?: SearchResponse;
  isLoading: boolean;
  error: string | null;
  clearSearch: () => void;
  searchChatRooms: (
    keyword: string,
    type: SearchRoomType,
  ) => Promise<unknown> | unknown;
};

export interface KeywordSearchConfig {
  key: string;
  type: SearchRoomType;
  title: string;
  response?: SearchResponse;
  isLoading: boolean;
  error: string | null;
  clearSearch: () => void;
  search: (keyword: string, type: SearchRoomType) => Promise<unknown> | unknown;
  mapRooms: (rooms: ChatRoomResponse[]) => ChatRoomListItem[];
}

const buildSearchHitReasons = (
  room: ChatRoomResponse,
  matchedRoomIds: Set<number>,
): string[] => {
  const reasons = [...(room.hit_reasons ?? [])];
  if (matchedRoomIds.has(room.id)) {
    reasons.push("content");
  }
  return reasons;
};

export const buildKeywordSearchConfigs = ({
  t,
  dmSearch,
  topicSearch,
  dmEntryMatchedRoomIds,
  topicEntryMatchedRoomIds,
  lastMessageMaxLength,
  buildHitReasonBadges,
}: {
  t: (key: string) => string;
  dmSearch: SearchState;
  topicSearch: SearchState;
  dmEntryMatchedRoomIds: Set<number>;
  topicEntryMatchedRoomIds: Set<number>;
  lastMessageMaxLength: number;
  buildHitReasonBadges: (hitReasons?: string[]) => ReactNode;
}): KeywordSearchConfig[] => [
  {
    key: "messages",
    type: "user_dm",
    title: t("chat.sectionMessages"),
    response: dmSearch.response,
    isLoading: dmSearch.isLoading,
    error: dmSearch.error,
    clearSearch: dmSearch.clearSearch,
    search: dmSearch.searchChatRooms,
    mapRooms: (rooms: ChatRoomResponse[]) =>
      mapChatRoomsToListItems(rooms, {
        maxLastEntryLength: lastMessageMaxLength,
      }).map((room, index) => ({
        ...room,
        sublabelRightContent: buildHitReasonBadges(
          buildSearchHitReasons(rooms[index], dmEntryMatchedRoomIds),
        ),
      })),
  },
  {
    key: "topics",
    type: "user_group",
    title: t("chat.sectionTopics"),
    response: topicSearch.response,
    isLoading: topicSearch.isLoading,
    error: topicSearch.error,
    clearSearch: topicSearch.clearSearch,
    search: topicSearch.searchChatRooms,
    mapRooms: (rooms: ChatRoomResponse[]) =>
      rooms.map((room) => ({
        id: room.id,
        title: room.title,
        imageUrl: room.image_url,
        lastEntryContent: undefined,
        sublabel: (
          <ChatRoomParticipantsBadges displayTitle={room.display_title} />
        ),
        sublabelRightContent: buildHitReasonBadges(
          buildSearchHitReasons(room, topicEntryMatchedRoomIds),
        ),
      })),
  },
];
